<template>
  <div class="chart-wrapper">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4
        v-if="title"
        class="font-weight-bold mb-0"
      >
        {{ title }}
      </h4>
      <div class="d-flex h-100">
        <div 
          v-for="(dataset, index) in chartData.datasets" 
          :key="index"
          class="card shadow-none d-flex flex-row align-items-center py-1 mb-0"
          :class="index !== chartData.datasets.length - 1 ? 'mr-4' : ''"
        >
          <div
            class="p-1 m-2 rounded-sm"
            :style="{ backgroundColor: dataset.borderColor || dataset.backgroundColor }"
          />
          <span class="mx-2">{{ dataset.label }}</span>
        </div>
      </div>
    </div>
    <div style="height: 250px;">
      <canvas ref="canvas" />
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'

export default {
  name: 'BarComboChart',
  extends: Bar,
  props: {
    title: {
      type: String,
      required: false
    },
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({
        responsive: true,
        maintainAspectRatio: false,
        title: { display: false },
        legend: { display: false },
        layout: {
          padding: {
            top: 10
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          position: 'nearest'
        },
        scales: {
          xAxes: [{
            display: true,
            gridLines: {
              display: true,
              color: '#E6F0F1'
            },
            ticks: {
              fontFamily: 'Nunito Sans, arial, serif',
              fontColor: '#676767'
            }
          }],
          yAxes: [
            {
              id: 'y-axis-1',
              type: 'linear',
              display: true,
              position: 'left',
              gridLines: {
                display: true,
                color: '#E6F0F1'
              },
              ticks: {
                fontFamily: 'Nunito Sans, arial, serif',
                fontColor: '#0F2C29'
              }
            },
            {
              id: 'y-axis-2',
              type: 'linear',
              display: true,
              position: 'left',
              gridLines: {
                drawOnChartArea: false,
              },
              ticks: {
                fontFamily: 'Nunito Sans, arial, serif',
                fontColor: '#22D3C5'
              }
            }
          ]
        },
      })
    }
  },
  data() {
    return {
      debounceTimer: null,
      isChartReady: false
    }
  },
  watch: {
    chartData: {
      handler(newData) {
        if (!this.isValidChartData(newData)) {
          return;
        }
        
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        
        this.debounceTimer = setTimeout(() => {
          this.updateChart();
        }, 500);
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.isValidChartData(this.chartData)) {
        this.initializeChart();
      }
    });
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.$data._chart) {
      this.$data._chart.destroy();
    }
  },
  methods: {
    isValidChartData(data) {
      return data && 
             data.labels && 
             data.datasets && 
             data.labels.length > 0 &&
             data.datasets.length > 0 &&
             data.datasets.every(ds => ds.data && ds.data.length > 0);
    },
    
    initializeChart() {
      if (this.isChartReady) {
        return;
      }
      this.renderChartWithGradient();
      this.isChartReady = true;
    },
    
    updateChart() {
      if (!this.isValidChartData(this.chartData)) {
        return;
      }

      if (this.$data._chart && this.isChartReady) {
        try {
          const processedData = this.processChartData();
          this.$data._chart.data = processedData;
          this.$data._chart.update();
        } catch (error) {
          console.warn('Chart update failed, recreating:', error);
          this.recreateChart();
        }
      } else {
        this.recreateChart();
      }
    },
    
    recreateChart() {
      if (this.$data._chart) {
        this.$data._chart.destroy();
        this.$data._chart = null;
      }
      
      this.isChartReady = false;
      
      this.$nextTick(() => {
        this.initializeChart();
      });
    },
    
    processChartData() {
      const processedData = JSON.parse(JSON.stringify(this.chartData));
      
      if (this.$refs.canvas) {
        const ctx = this.$refs.canvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(34, 211, 197, 0.5)');
        gradient.addColorStop(1, 'rgba(34, 211, 197, 0)');
        
        processedData.datasets.forEach(dataset => {
          if (dataset.type === 'line') {
            dataset.backgroundColor = gradient;
          }
        });
      }
      
      return processedData;
    },
    
    renderChartWithGradient() {
      if (!this.$refs.canvas) {
        console.warn('Canvas ref not available');
        return;
      }
      
      const processedData = this.processChartData();
      this.renderChart(processedData, this.options);
    }
  }
}
</script>